import { inject, InjectionToken, ProviderToken } from '@angular/core';
import { EntityState, EntityStore } from '@datorama/akita';
import { InterfaceNameValue } from '@common/interfaces/issueTypeInterface';
import { IBaseInterfaceData } from '@common/interfaces/base';
import { ID } from '@common/interfaces/id';
// import type: valore usato solo nei tipi. crudService importa updateStoreStrategies, che importa
// questo modulo: un value-import reintrodurrebbe il ciclo di moduli che questo registry elimina
// (prima: interfaceDataMapping -> *.service -> updateStoreStrategies -> interfaceDataMapping).
import type { AbstractInterfaceDataQuery } from './interfaceDataModel';
import type { CrudService } from './crudService';

/**
 * Registry of the store/query/service trio of every issue interface (IID_*), keyed by
 * InterfaceName. It replaces the three literal maps of interfaceDataMapping.ts: the trio
 * self-registers through the `@InterfaceDataStore` / `@InterfaceDataQuery` / `@InterfaceDataService`
 * decorators (open/closed, same rationale as `@PersistedStoreConfig` — see persist-registry.ts),
 * mirroring the backend's `@OMEntity` / `@OMEntityService` / `@OMEntityRepository`
 * (backend/src/modules/shared/metadata/relation.decorators.ts).
 *
 * Timing: the decorators run at module evaluation. Nothing else imports the iid* modules, so
 * interfaceData.barrel.ts must keep them in main.ts's eager import graph — a missing barrel line
 * means an unregistered trio (and a store missing from the persist hydration gate count).
 *
 * DI integration: consumers never receive classes from here. They resolve instances through the
 * lazily-created InjectionTokens below (one per interface and kind, memoized so that TestBed
 * overrides and the root injector cache work), normally via InterfaceDataResolver. The token
 * factory runs at first injection — long after every decorator — and defers to Angular DI with the
 * registered class, which stays `@Injectable({providedIn: 'root'})`.
 *
 * Meta interfaces ('_meta_*') never go through here: they are created at runtime by
 * MetaInterfaceServiceProvider and every caller dispatches them BEFORE the registry lookup.
 */

export type AnyInterfaceDataStore = EntityStore<EntityState<IBaseInterfaceData, ID>>;
export type AnyInterfaceDataQuery = AbstractInterfaceDataQuery<IBaseInterfaceData>;
export type AnyInterfaceDataService = CrudService<IBaseInterfaceData, AnyInterfaceDataStore, AnyInterfaceDataQuery>;

export interface InterfaceDataRegistration {
  interfaceName: InterfaceNameValue;
  store?: ProviderToken<AnyInterfaceDataStore>;
  query?: ProviderToken<AnyInterfaceDataQuery>;
  service?: ProviderToken<AnyInterfaceDataService>;
}

type RegistrationKind = 'store' | 'query' | 'service';

const registrations = new Map<InterfaceNameValue, InterfaceDataRegistration>();

const DECORATOR_BY_KIND: Record<RegistrationKind, string> = {
  store: '@InterfaceDataStore',
  query: '@InterfaceDataQuery',
  service: '@InterfaceDataService',
};

function register(kind: RegistrationKind, interfaceName: InterfaceNameValue, target: ProviderToken<unknown>): void {
  const registration = registrations.get(interfaceName) ?? { interfaceName };
  if (registration[kind]) {
    console.error(`[INTERFACE DATA REGISTRY] ${kind} registrata due volte per '${interfaceName}': registrazione ignorata`);
    return;
  }
  registration[kind] = target as ProviderToken<never>;
  registrations.set(interfaceName, registration);
}

function getRegisteredClass<T>(kind: RegistrationKind, interfaceName: InterfaceNameValue): ProviderToken<T> {
  const token = registrations.get(interfaceName)?.[kind];
  if (!token) {
    throw new Error(
      `[INTERFACE DATA REGISTRY] nessuna ${kind} registrata per '${interfaceName}': ` +
      `manca ${DECORATOR_BY_KIND[kind]} o la riga in interfaceData.barrel.ts?`,
    );
  }
  return token as ProviderToken<T>;
}

/**
 * Class decorators for the trio of an issue interface. They only record the class in the registry
 * and return it UNCHANGED (no wrapping/subclassing: Angular's AOT-compiled metadata on the class
 * must not be disturbed — same contract as `@PersistedStoreConfig`). Stack them below `@Injectable`.
 */
// Decorator factories: PascalCase on purpose, like Akita's StoreConfig.
/* eslint-disable @typescript-eslint/naming-convention */
export function InterfaceDataStore(interfaceName: InterfaceNameValue): ClassDecorator {
  return (target) => { register('store', interfaceName, target); };
}

export function InterfaceDataQuery(interfaceName: InterfaceNameValue): ClassDecorator {
  return (target) => { register('query', interfaceName, target); };
}

export function InterfaceDataService(interfaceName: InterfaceNameValue): ClassDecorator {
  return (target) => { register('service', interfaceName, target); };
}
/* eslint-enable @typescript-eslint/naming-convention */

const queryTokens = new Map<InterfaceNameValue, InjectionToken<AnyInterfaceDataQuery>>();
const serviceTokens = new Map<InterfaceNameValue, InjectionToken<AnyInterfaceDataService>>();

/**
 * Memoized per interfaceName: the same token must be returned on every call, or the root-injector
 * cache and TestBed overrides (`{ provide: interfaceDataQueryToken(name), useValue: ... }`) would
 * not match. The factory resolves the registered class through DI at first injection.
 * No store token: nothing injects store instances by interface name (stores reach consumers
 * through their query/service); the registered store class only provides static metadata.
 */
export function interfaceDataQueryToken(interfaceName: InterfaceNameValue): InjectionToken<AnyInterfaceDataQuery> {
  let token = queryTokens.get(interfaceName);
  if (!token) {
    token = new InjectionToken(`INTERFACE_DATA_QUERY_${interfaceName}`, {
      providedIn: 'root',
      factory: () => inject(getRegisteredClass<AnyInterfaceDataQuery>('query', interfaceName)),
    });
    queryTokens.set(interfaceName, token);
  }
  return token;
}

export function interfaceDataServiceToken(interfaceName: InterfaceNameValue): InjectionToken<AnyInterfaceDataService> {
  let token = serviceTokens.get(interfaceName);
  if (!token) {
    token = new InjectionToken(`INTERFACE_DATA_SERVICE_${interfaceName}`, {
      providedIn: 'root',
      factory: () => inject(getRegisteredClass<AnyInterfaceDataService>('service', interfaceName)),
    });
    serviceTokens.set(interfaceName, token);
  }
  return token;
}

export const isRegisteredInterfaceName = (interfaceName: InterfaceNameValue): boolean =>
  registrations.has(interfaceName);

export const getRegisteredInterfaceNames = (): readonly InterfaceNameValue[] =>
  Array.from(registrations.keys());

/** interfaceName -> Akita store name, read from the static config written by `@StoreConfig`. */
export const getInterfaceStoreNamesByInterfaceName = (): Record<string, string> => {
  const acc: Record<string, string> = {};
  for (const [interfaceName, registration] of registrations) {
    const storeName = (registration.store as { akitaConfig?: { storeName?: string } } | undefined)?.akitaConfig?.storeName;
    if (storeName) {
      acc[interfaceName] = storeName;
    }
  }
  return acc;
};

/** Full registrations, for the completeness safety-net spec. */
export const getInterfaceDataRegistrations = (): readonly InterfaceDataRegistration[] =>
  Array.from(registrations.values());
